import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { FiX } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { getTenantRiskFactors } from "../../../../shared/services/apis";
import { FinancialPositionRiskCard } from "./FinancialPosition /FinancialPositionCard";
import { ForecastsRiskCard } from "./ForcastsRisk/Forecasts";
import IdiosyncraticPropertyRisksCard from "./IdiosyncraticPropertyRisks/IdiosyncraticPropertyRisksCard";
import MarketPositionCard from "./MarketPositionCard/MarketPositionCard";
import Summary from "./summary/Summary";
import BankMarketPositionCard from "../BankRiskScore/MarketPositionCard/MarketPositionCard";

const makeSectionId = (key: string) =>
  `section-${key
    .replace(/[^\w]+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase()}`;

const ObservedSection = ({
  sectionKey,
  sectionId,
  children,
  className = "",
}: {
  sectionKey: string;
  sectionId?: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const id = sectionId ?? makeSectionId(sectionKey);

  return (
    <section
      id={id}
      className={`min-h-screen scroll-mt-20 ${className}`}
      data-section-key={sectionKey}
    >
      {children}
    </section>
  );
};

export function RiskScoreLayout() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenantServices, setTenantServices] = useState<any[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tenantServiceId = searchParams.get("tenantServiceId") || "";
  const tenantServiceOptionId = searchParams.get("tenantServiceOptionId") || "";

  const mainContentRef = useRef<HTMLDivElement>(null!);
  const lastUpdateRef = useRef<string>("");

  // Scroll to section (inside the scrollable container)
  const scrollToSection = useCallback((sectionId: string) => {
    if (!mainContentRef.current) return;
    const container = mainContentRef.current;
    const element = container.querySelector(
      `#${sectionId}`
    ) as HTMLElement | null;

    if (!element) {
      console.warn("[scrollToSection] element not found:", sectionId);
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const elementTop = element.getBoundingClientRect().top;
    const scrollTop = container.scrollTop;
    const yOffset = 80;

    container.scrollTo({
      top: scrollTop + (elementTop - containerTop) - yOffset,
      behavior: "smooth",
    });
  }, []);

  // Track which section is in view during scroll
  const handleScroll = useCallback(() => {
    if (!mainContentRef.current) return;

    const container = mainContentRef.current;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const viewportCenter = scrollTop + containerHeight / 2;

    const sections = container.querySelectorAll("[data-section-key]");
    let closestSection: { key: string; id: string; distance: number } | null =
      null;

    sections.forEach((section: Element) => {
      const sectionKey = section.getAttribute("data-section-key");
      const sectionId = section.id;
      if (!sectionKey) return;

      const rect = section.getBoundingClientRect();
      const sectionTop = scrollTop + rect.top;
      const sectionCenter = sectionTop + rect.height / 2;
      const distance = Math.abs(viewportCenter - sectionCenter);

      if (!closestSection || distance < closestSection.distance) {
        closestSection = { key: sectionKey, id: sectionId, distance };
      }
    });

    // Update URL only if section changed and avoid too frequent updates
    if (closestSection) {
      // narrow type explicitly to satisfy TypeScript
      const currentKey = (
        closestSection as {
          key: string;
          id: string;
          distance: number;
        }
      ).key;

      if (currentKey !== lastUpdateRef.current) {
        lastUpdateRef.current = currentKey;

        if (currentKey.toLowerCase() === "summary") {
          if (tenantServiceOptionId.toLowerCase() !== "summary") {
            setSearchParams(
              {
                tenantServiceId,
                tenantServiceOptionId: "summary",
                tenantServiceOptionKey: "Summary",
              },
              { replace: true }
            );
          }
        } else {
          const option = tenantServices.find(
            (s) => s.key.toLowerCase() === currentKey.toLowerCase()
          );
          if (option && option.id !== tenantServiceOptionId) {
            setSearchParams(
              {
                tenantServiceId,
                tenantServiceOptionId: option.id,
                tenantServiceOptionKey: option.key,
              },
              { replace: true }
            );
          }
        }
      }
    }
  }, [tenantServices, tenantServiceId, tenantServiceOptionId, setSearchParams]);

  // Add scroll listener
  useEffect(() => {
    const container = mainContentRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const renderCard = (key: string, id: string) => {
    switch (key) {
      case "Financial Position":
        return <FinancialPositionRiskCard RiskFactorId={id} />;
      case "Forecasts":
        return <ForecastsRiskCard RiskFactorId={id} />;
      case "Idiosyncratic property risks":
        return <IdiosyncraticPropertyRisksCard RiskFactorId={id} />;
      case "Market position":
        return <MarketPositionCard RiskFactorId={id} />;
      case "Summary":
        return <Summary RiskFactorId={id} />;
      case "Industry Risk":
        return <BankMarketPositionCard RiskFactorId={id} />;
      case "Financial Risk":
        return <BankMarketPositionCard RiskFactorId={id} />;
      case "Business Risk":
        return <BankMarketPositionCard RiskFactorId={id} />;
      case "Management Risk":
        return <BankMarketPositionCard RiskFactorId={id} />;
      default:
        return null;
    }
  };

  // Helper: go to a section by its key (Summary will be handled even if it's not in tenantServices)
  const handleGoToSectionByKey = useCallback(
    (key: string) => {
      const option = tenantServices.find(
        (s) => s.key.toLowerCase() === key.toLowerCase()
      );
      const sectionId = option ? option.sectionId : makeSectionId(key);
      const optionId = option ? option.id : key.toLowerCase(); // fallback id for search params

      // Update last update ref to prevent scroll listener from triggering
      lastUpdateRef.current = key;

      setSearchParams(
        {
          tenantServiceId,
          tenantServiceOptionId: optionId,
          tenantServiceOptionKey: key,
        },
        { replace: true }
      );
      scrollToSection(sectionId);
      setSidebarOpen(false);
    },
    [tenantServices, tenantServiceId, setSearchParams, scrollToSection]
  );

  // Fetch tenant risk factors
  useEffect(() => {
    const fetchRiskFactors = async () => {
      if (!tenantId) return;
      try {
        const response = await getTenantRiskFactors(tenantId);
        const riskFactors = response.data || response;
        setTenantServices(
          riskFactors.map((factor: any) => {
            const name = factor.name || String(factor.id);
            return {
              id: String(factor.id),
              key: name,
              name,
              sectionId: makeSectionId(name),
            };
          })
        );

        // if (!tenantServiceOptionId && riskFactors.length > 0) {
        //   const defaultOption = riskFactors[riskFactors.length - 1];
        //   const defaultName = defaultOption.name;
        //   setSearchParams(
        //     {
        //       tenantServiceId: tenantServiceId,
        //       tenantServiceOptionId: String(defaultOption.id),
        //       tenantServiceOptionKey: defaultName,
        //     },
        //     { replace: true }
        //   );
        //   lastUpdateRef.current = defaultName;
        // }
        if (!tenantServiceOptionId) {
          setSearchParams(
            {
              tenantServiceId,
              tenantServiceOptionId: "summary",
              tenantServiceOptionKey: "Summary",
            },
            { replace: true }
          );
          lastUpdateRef.current = "Summary";
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchRiskFactors();
  }, [tenantId]); //tenantServiceId, tenantServiceOptionId, setSearchParams

  const ProcessingSkeleton = () => {
    const [progress, setProgress] = useState(60);
    const [currentStep, setCurrentStep] = useState(0);
    const [timeElapsed, setTimeElapsed] = useState(0);

    const steps = [
      "Uploading documents...",
      "Extracting content...",
      "Analyzing risk factors...",
      "Generating insights...",
      "Finalizing results...",
    ];

    useEffect(() => {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + Math.random() * 2;
          return newProgress > 95 ? 95 : newProgress;
        });
      }, 2000);

      const stepInterval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % steps.length);
      }, 4000);

      const timeInterval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);

      return () => {
        clearInterval(progressInterval);
        clearInterval(stepInterval);
        clearInterval(timeInterval);
      };
    }, []);

    const formatTime = (seconds: any) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
      <div className="flex items-center justify-center min-h-[40vh] bg-[#FDFDFD]">
        <div className="flex flex-col items-center justify-center bg-[#FDFDFD] border border-gray-300 shadow-lg rounded-xl p-6 w-[90%] max-w-md relative overflow-hidden">
          {/* Subtle background animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-50 to-transparent opacity-30 animate-pulse"></div>

          {/* Compact animated processing icon */}
          <div className="relative mb-4 z-10">
            <div className="w-12 h-12 border-3 border-gray-300 rounded-full animate-spin">
              <div
                className="absolute top-0 left-0 w-12 h-12 border-3 border-transparent border-t-gray-600 rounded-full animate-spin"
                style={{ animationDuration: "1s" }}
              ></div>
            </div>
            {/* Inner pulsing circle */}
            <div className="absolute top-1.5 left-1.5 w-9 h-9 bg-gray-200 rounded-full animate-ping opacity-20"></div>
          </div>

          {/* Main heading */}
          <h2 className="text-xl font-semibold text-gray-800 mb-3 text-center z-10">
            Processing Your Data
          </h2>

          {/* Compact info row */}
          <div className="flex items-center justify-between w-full mb-3 z-10 text-sm">
            <span className="text-gray-600 font-mono">
              {formatTime(timeElapsed)}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-600">{Math.round(progress)}%</span>
          </div>

          {/* Current step indicator - more compact */}
          <div className="bg-gray-100 rounded-lg px-3 py-1.5 mb-3 z-10 transition-all duration-500 w-full">
            <p className="text-gray-700 text-xs font-medium flex items-center justify-center">
              <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 animate-pulse"></span>
              {steps[currentStep]}
            </p>
          </div>

          {/* Enhanced progress bar */}
          <div className="w-full bg-gray-300 rounded-full h-2 mb-2 z-10 shadow-inner">
            <div
              className="bg-gradient-to-r from-gray-600 to-gray-700 h-2 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
            </div>
          </div>

          {/* Compact message */}
          <p className="text-gray-500 text-center text-xs z-10 flex items-center justify-center gap-1">
            <span>Estimated: 15-30 min</span>
            <span className="flex">
              <span
                className="animate-bounce inline-block"
                style={{ animationDelay: "0ms" }}
              >
                .
              </span>
              <span
                className="animate-bounce inline-block"
                style={{ animationDelay: "150ms" }}
              >
                .
              </span>
              <span
                className="animate-bounce inline-block"
                style={{ animationDelay: "300ms" }}
              >
                .
              </span>
            </span>
          </p>

          {/* Floating particles animation - smaller */}
          <div
            className="absolute top-3 left-3 w-0.5 h-0.5 bg-gray-400 rounded-full animate-ping opacity-40"
            style={{ animationDelay: "0s" }}
          ></div>
          <div
            className="absolute top-5 right-4 w-0.5 h-0.5 bg-gray-400 rounded-full animate-ping opacity-30"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute bottom-5 left-5 w-0.5 h-0.5 bg-gray-400 rounded-full animate-ping opacity-35"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>
      </div>
    );
  };

  if (!tenantId) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Tenant Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            Please provide a valid tenant ID.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const hasSummaryInServices = tenantServices.some(
    (s) => s.key.toLowerCase() === "summary"
  );

  return (
    <div className="relative flex flex-col md:flex-row max-h-screen bg-[#FDFDFD]">
      {/* Mobile toggle button */}
      <button
        className="md:hidden fixed top-4 mt-12 left-4 z-50 p-2 bg-white rounded-md shadow-md cursor-pointer"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle sidebar"
      >
        {sidebarOpen ? <FiX size={20} /> : <BsThreeDotsVertical size={20} />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed top-24 left-0 h-full min-w-[220px] transform transition-transform duration-300 z-40 ${
          sidebarOpen ? "translate-x-0 bg-white shadow-md" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="min-h-screen p-4 flex flex-col justify-center overflow-y-auto">
          {tenantServices.length > 0 && (
            <button
              onClick={() => handleGoToSectionByKey("Summary")}
              className={`text-md text-left px-4 py-2 w-full cursor-pointer flex items-center capitalize transition-colors duration-300 ${
                tenantServiceOptionId.toLowerCase() === "summary"
                  ? "text-gray-800 font-medium"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {"Summary"}
              {tenantServiceOptionId.toLowerCase() === "summary" && (
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 shadow-[0_0_20px_rgba(168,85,247,0.6)] blur-[0.6px] ml-2"></div>
              )}
            </button>
          )}

          {tenantServices
            ?.slice()
            .reverse()
            .map((option) => (
              <div
                key={option.id}
                className={`flex items-center justify-between rounded-lg ${
                  tenantServiceOptionId === option.id
                    ? "text-gray-800 font-medium"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                <button
                  onClick={() => handleGoToSectionByKey(option.key)}
                  className="text-md text-left px-4 py-2 w-full cursor-pointer flex items-center capitalize transition-colors duration-300"
                >
                  {option.name}
                  {tenantServiceOptionId === option.id && (
                    <div className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 shadow-[0_0_20px_rgba(168,85,247,0.6)] blur-[0.6px] ml-2 "></div>
                  )}
                </button>
              </div>
            ))}
        </div>
      </div>

      {/* Main Content */}
      <main
        ref={mainContentRef}
        className="flex-1 px-2 sm:ml-45 2xl:ml-0 h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide"
      >
        <div className="mt-6 mb-50">
          {!hasSummaryInServices && tenantServices.length > 0 && (
            <ObservedSection
              key="summary-synthetic"
              sectionKey="Summary"
              sectionId={makeSectionId("Summary")}
              className="mb-8"
            >
              {renderCard("Summary", "summary")}
            </ObservedSection>
          )}

          {tenantServices
            ?.slice()
            .reverse()
            .map((option) => (
              <ObservedSection
                key={option.id}
                sectionKey={option.key}
                sectionId={option.sectionId}
                className="mb-8"
              >
                {renderCard(option.key, option.id)}
              </ObservedSection>
            ))}

          {tenantServices.length <= 0 && <ProcessingSkeleton />}
        </div>
      </main>
    </div>
  );
}
